// A 2.5D photographic turn, not a measured 3D model. The source image and
// silhouette stay unchanged; subtle surface detail moves cylindrically while
// the photographed top mesh rotates in its own elliptical plane.
window.HydraTrailFilterTwist = async function createFilterTwist(cap) {
  const image = cap.querySelector('.ht-filter-cap-photo');
  const canvas = cap.querySelector('.ht-filter-cap-twist');
  await image.decode();
  const gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: false, antialias: false });
  if (!gl) return null;
  const vertexSource = `
    attribute vec2 position;
    varying vec2 uv;
    void main() {
      uv = vec2((position.x + 1.0) * .5, (1.0 - position.y) * .5);
      gl_Position = vec4(position, 0.0, 1.0);
    }`;
  const fragmentSource = `
    precision mediump float;
    uniform sampler2D photograph;
    uniform sampler2D rowBounds;
    uniform float turn;
    varying vec2 uv;
    void main() {
      vec4 original = texture2D(photograph, uv);
      if (uv.y > .31 || original.a < .003) { gl_FragColor = vec4(0.0); return; }
      vec4 bounds = texture2D(rowBounds, vec2(.5, uv.y));
      float centre = (bounds.r + bounds.g) * .5;
      float radius = max((bounds.g - bounds.r) * .5, .001);
      float localX = clamp((uv.x - centre) / radius, -.985, .985);
      float angle = asin(localX) + turn;
      float sourceX = centre + radius * sin(angle) * .985;
      vec2 surfaceUV = vec2(sourceX, uv.y);
      vec4 surface = texture2D(photograph, surfaceUV);
      vec3 average = (
        texture2D(photograph, surfaceUV + vec2(.009, 0.0)).rgb +
        texture2D(photograph, surfaceUV - vec2(.009, 0.0)).rgb +
        texture2D(photograph, surfaceUV + vec2(0.0, .003)).rgb +
        texture2D(photograph, surfaceUV - vec2(0.0, .003)).rgb
      ) * .25;
      // Rotate the actual blue mesh within the top face; leave its rim intact.
      vec2 topCentre = vec2(.502, .116);
      vec2 topRadius = vec2(.113, .0195);
      vec2 top = (uv - topCentre) / topRadius;
      float topDistance = length(top);
      mat2 rotation = mat2(cos(turn), -sin(turn), sin(turn), cos(turn));
      vec2 meshUV = topCentre + (rotation * top) * topRadius;
      vec4 mesh = texture2D(photograph, meshUV);
      float meshMask = 1.0 - smoothstep(.90, 1.0, topDistance);
      // Retain all photographed thread geometry and lighting. Move only a
      // restrained layer of fine surface detail around the cylindrical face.
      vec3 detail = clamp(surface.rgb - average, vec3(-.055), vec3(.055));
      float detailMask = smoothstep(.16, .19, uv.y) * (1.0 - smoothstep(.82, .99, abs(localX))) * step(.98, surface.a);
      vec3 colour = clamp(original.rgb + detail * .35 * detailMask, 0.0, 1.0);
      colour = mix(colour, mesh.rgb, meshMask);
      gl_FragColor = vec4(colour, original.a);
    }`;

  function shader(type, source) {
    const result = gl.createShader(type);
    gl.shaderSource(result, source);
    gl.compileShader(result);
    if (!gl.getShaderParameter(result, gl.COMPILE_STATUS)) throw new Error('Filter twist shader unavailable');
    return result;
  }
  const program = gl.createProgram();
  gl.attachShader(program, shader(gl.VERTEX_SHADER, vertexSource));
  gl.attachShader(program, shader(gl.FRAGMENT_SHADER, fragmentSource));
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
  gl.useProgram(program);
  const vertices = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, vertices);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]), gl.STATIC_DRAW);
  const position = gl.getAttribLocation(program, 'position');
  gl.enableVertexAttribArray(position);
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

  function texture(unit, uniform) {
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, gl.createTexture());
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.uniform1i(gl.getUniformLocation(program, uniform), unit);
  }
  texture(0, 'photograph');
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);

  // Read the alpha silhouette once, so every row retains the photographed
  // collar, threads and changing diameter while only the surface turns.
  const source = document.createElement('canvas');
  source.width = image.naturalWidth;
  source.height = image.naturalHeight;
  const context = source.getContext('2d', { willReadFrequently: true });
  context.drawImage(image, 0, 0);
  const pixels = context.getImageData(0, 0, source.width, source.height).data;
  const bounds = new Uint8Array(source.height * 4);
  for (let y = 0; y < source.height; y++) {
    let left = source.width, right = 0;
    for (let x = 0; x < source.width; x++) {
      if (pixels[(y * source.width + x) * 4 + 3] > 192) {
        left = Math.min(left, x);
        right = Math.max(right, x);
      }
    }
    bounds[y * 4] = Math.round(left / source.width * 255);
    bounds[y * 4 + 1] = Math.round(right / source.width * 255);
    bounds[y * 4 + 3] = 255;
  }
  texture(1, 'rowBounds');
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, source.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, bounds);
  const turn = gl.getUniformLocation(program, 'turn');
  gl.viewport(0, 0, canvas.width, canvas.height);
  gl.disable(gl.BLEND);
  let stopped = false;
  canvas.addEventListener('webglcontextlost', event => {
    event.preventDefault();
    stopped = true;
    canvas.hidden = true;
  });
  return {
    draw(radians, opacity) {
      if (stopped) return;
      canvas.hidden = false;
      canvas.style.opacity = String(opacity);
      gl.uniform1f(turn, radians);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    },
    hide() { canvas.hidden = true; }
  };
};

(() => {
  if (window.HTFilterAnimationLoaded) return;
  window.HTFilterAnimationLoaded = true;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initialise(root) {
  root.querySelectorAll('[data-ht-filter-presentation]').forEach(presentation => {
    if (presentation.dataset.htFilterInitialised) return;
    presentation.dataset.htFilterInitialised = 'true';
    const figure = presentation.querySelector('.ht-filter-figure');
    const cap = presentation.querySelector('.ht-filter-piece--cap');
    const labels = [...presentation.querySelectorAll('.ht-filter-label')];
    const controls = presentation.querySelector('.ht-filter-controls');
    const replay = presentation.querySelector('[data-ht-filter-replay]');
    const still = presentation.querySelector('[data-ht-filter-still]');
    const cue = presentation.querySelector('.ht-filter-twist-cue');
    const arc = presentation.querySelector('.ht-filter-twist-arc');
    const arrow = presentation.querySelector('.ht-filter-twist-arrow');
    let animations = [];
    let run = 0;
    let played = false;
    let staticChosen = false;
    let observer;
    let twist;
    let frame;

    function settle() {
      run += 1;
      figure.dataset.motionState = reducedMotion.matches ? 'reduced' : 'settled';
      animations.forEach(animation => animation.cancel());
      animations = [];
      cancelAnimationFrame(frame);
      twist?.hide();
      cue.style.opacity = '0';
      replay.disabled = false;
      replay.textContent = 'Replay animation';
      still.setAttribute('aria-pressed', String(staticChosen));
    }

    function play() {
      if (reducedMotion.matches || typeof cap.animate !== 'function') return;
      settle();
      staticChosen = false;
      played = true;
      still.setAttribute('aria-pressed', 'false');
      figure.dataset.motionState = 'playing';
      replay.disabled = true;
      replay.textContent = 'Playing…';
      const thisRun = run;
      const started = performance.now();
      function drawTurn(now) {
        if (thisRun !== run) return;
        const elapsed = now - started;
        const progress = Math.max(0, Math.min(1, (elapsed - 250) / 2300));
        const eased = progress * progress * (3 - 2 * progress);
        const fade = Math.min(1, progress / .08, (1 - progress) / .08);
        cue.style.opacity = String(fade * .8);
        arc.style.strokeDashoffset = String(1 - Math.min(1, progress / .78));
        arrow.style.opacity = String(Math.max(0, Math.min(1, (progress - .68) / .12)));
        if (progress > 0 && progress < 1) twist?.draw(-Math.PI * 2 * eased, fade);
        else twist?.hide();
        if (elapsed < 2700) frame = requestAnimationFrame(drawTurn);
      }
      frame = requestAnimationFrame(drawTurn);
      animations = [
        cap.animate([
          { transform: 'translateY(13.7%)', offset: 0 },
          { transform: 'translateY(13.7%)', offset: .07, easing: 'ease-in-out' },
          { transform: 'translateY(10.5%)', offset: .72, easing: 'cubic-bezier(.3,0,.2,1)' },
          { transform: 'translateY(0)', offset: 1 }
        ], { duration: 3550, fill: 'both' }),
        ...labels.map((label, index) => label.animate([
          { opacity: 0, transform: 'translateY(4px)' },
          { opacity: 1, transform: 'translateY(0)' }
        ], { duration: 600, delay: 3100 + index * 200, easing: 'ease-out', fill: 'both' }))
      ];
      Promise.all(animations.map(animation => animation.finished)).then(() => {
        if (run === thisRun) settle();
      }).catch(() => { /* Cancellation is expected when Still image is selected. */ });
    }

    replay.addEventListener('click', play);
    still.addEventListener('click', () => {
      played = true;
      staticChosen = true;
      observer?.disconnect();
      settle();
    });

    const motionAvailable = typeof cap.animate === 'function';
    function syncPreference() {
      controls.hidden = reducedMotion.matches || !motionAvailable;
      if (reducedMotion.matches) {
        played = true;
        observer?.disconnect();
        settle();
      }
    }
    reducedMotion.addEventListener('change', syncPreference);
    syncPreference();

    // With no JavaScript, reduced motion, or no animation API, the full still
    // image and both labels remain visible. Only this photograph is animated.
    if (reducedMotion.matches || !motionAvailable) return;
    Promise.all([...figure.querySelectorAll('img')].map(image => image.decode())).then(async () => {
      try { twist = await window.HydraTrailFilterTwist?.(cap); } catch { /* Keep the photo if graphics are unavailable. */ }
      cap.dataset.twistRenderer = twist ? 'photographic' : 'unavailable';
      if (played || staticChosen || reducedMotion.matches) return;
      figure.dataset.motionState = 'waiting';
      if (!('IntersectionObserver' in window)) {
        play();
        return;
      }
      observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= .45)) {
          observer.disconnect();
          if (!played && !staticChosen) play();
        }
      }, { threshold: .45 });
      observer.observe(figure);
    }).catch(settle);
  });
  }
  initialise(document);
  document.addEventListener('shopify:section:load', event => initialise(event.target));
})();
